import { axiosClient } from '@/libs/axios-client';

const BASE = '/fleet';

// ─── Settings ────────────────────────────────────────────────────────────────

export const getFleetSettings = async (companyId) => {
  const { data } = await axiosClient.get(`${BASE}/settings/${companyId}`);
  return data;
};

export const updateFleetSettings = async (companyId, payload) => {
  const { data } = await axiosClient.put(`${BASE}/settings/${companyId}`, payload);
  return data;
};

// ─── Vehicles ────────────────────────────────────────────────────────────────

export const listVehicles = async ({ companyId, status } = {}) => {
  const { data } = await axiosClient.get(`${BASE}/vehicles`, {
    params: { companyId, status },
  });
  return data;
};

export const createVehicle = async (payload) => {
  const { data } = await axiosClient.post(`${BASE}/vehicles`, payload);
  return data;
};

export const getVehicle = async (id) => {
  const { data } = await axiosClient.get(`${BASE}/vehicles/${id}`);
  return data;
};

export const updateVehicle = async (id, payload) => {
  const { data } = await axiosClient.put(`${BASE}/vehicles/${id}`, payload);
  return data;
};

export const deleteVehicle = async (id) => {
  const { data } = await axiosClient.delete(`${BASE}/vehicles/${id}`);
  return data;
};

export const uploadVehicleDocument = async (id, file, label) => {
  const formData = new FormData();
  formData.append('file', file, file.name);
  if (label) formData.append('label', label);
  const { data } = await axiosClient.post(
    `${BASE}/vehicles/${id}/documents`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
};

export const getVehicleDocumentUrl = async (id, docId) => {
  const { data } = await axiosClient.get(
    `${BASE}/vehicles/${id}/documents/${docId}/url`
  );
  return data;
};

export const deleteVehicleDocument = async (id, docId) => {
  const { data } = await axiosClient.delete(
    `${BASE}/vehicles/${id}/documents/${docId}`
  );
  return data;
};

export const addVehicleComment = async (id, text) => {
  const { data } = await axiosClient.post(
    `${BASE}/vehicles/${id}/comments`,
    { text }
  );
  return data;
};

export const deleteVehicleComment = async (id, commentId) => {
  const { data } = await axiosClient.delete(
    `${BASE}/vehicles/${id}/comments/${commentId}`
  );
  return data;
};

export const getVehicleEarnings = async (id, residualPercent, asOfDate) => {
  const params = {};
  if (residualPercent != null) params.residualPercent = residualPercent;
  if (asOfDate) params.asOfDate = asOfDate;
  const { data } = await axiosClient.get(`${BASE}/vehicles/${id}/earnings`, {
    params,
  });
  return data;
};

export const listVehicleInvoices = async (id) => {
  const { data } = await axiosClient.get(`${BASE}/vehicles/${id}/invoices`);
  return data;
};

// ─── Expenses ────────────────────────────────────────────────────────────────

export const listExpenses = async (filters = {}) => {
  const { data } = await axiosClient.get(`${BASE}/expenses`, { params: filters });
  return data;
};

export const createExpense = async (payload, file) => {
  if (file) {
    const formData = new FormData();
    formData.append('data', JSON.stringify(payload));
    formData.append('invoice', file, file.name);
    const { data } = await axiosClient.post(`${BASE}/expenses`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  }
  const { data } = await axiosClient.post(`${BASE}/expenses`, payload);
  return data;
};

export const updateExpense = async (id, payload) => {
  const { data } = await axiosClient.put(`${BASE}/expenses/${id}`, payload);
  return data;
};

export const deleteExpense = async (id) => {
  const { data } = await axiosClient.delete(`${BASE}/expenses/${id}`);
  return data;
};

export const uploadExpenseInvoice = async (id, file) => {
  const formData = new FormData();
  formData.append('file', file, file.name);
  const { data } = await axiosClient.post(
    `${BASE}/expenses/${id}/invoice`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
};

export const getExpenseInvoiceUrl = async (id) => {
  const { data } = await axiosClient.get(`${BASE}/expenses/${id}/invoice/url`);
  return data;
};

export const deleteExpenseInvoice = async (id) => {
  const { data } = await axiosClient.delete(`${BASE}/expenses/${id}/invoice`);
  return data;
};

// ─── Reminders ───────────────────────────────────────────────────────────────

export const listReminders = async ({ companyId, unreadOnly, limit } = {}) => {
  const { data } = await axiosClient.get(`${BASE}/reminders`, {
    params: { companyId, unreadOnly, limit },
  });
  return data;
};

export const markReminderRead = async (id) => {
  const { data } = await axiosClient.put(`${BASE}/reminders/${id}/read`);
  return data;
};

export const runReminderScannerNow = async () => {
  const { data } = await axiosClient.post(`${BASE}/reminders/run-now`, {});
  return data;
};

// ─── Summary ─────────────────────────────────────────────────────────────────

export const getFleetSummary = async ({
  companyId,
  residualPercent,
  asOfDate,
} = {}) => {
  const { data } = await axiosClient.get(`${BASE}/summary`, {
    params: { companyId, residualPercent, asOfDate },
  });
  return data;
};

export const exportSummaryExcel = async ({ companyId, residualPercent } = {}) => {
  const response = await axiosClient.get(`${BASE}/summary/export`, {
    params: { companyId, residualPercent },
    responseType: 'blob',
  });
  return response.data;
};
